import {
  READ_PURCHASE_CREDIT_NOTE,
  READ_PURCHASE_INVOICE,
  READ_SALE_CREDIT_NOTE,
  READ_SALE_INVOICE,
} from '../../../constants/eInvoicePermissions';
import { ALL_FILTER_VALUE } from '../constants/documentReportFilterConstants';

export const DOCUMENT_REPORT_SCOPE_PERMISSIONS = [
  { permission: READ_SALE_INVOICE, labelKey: 'scope_sales_invoices' },
  { permission: READ_SALE_CREDIT_NOTE, labelKey: 'scope_sales_credit_notes' },
  { permission: READ_PURCHASE_INVOICE, labelKey: 'scope_purchase_invoices' },
  { permission: READ_PURCHASE_CREDIT_NOTE, labelKey: 'scope_purchase_credit_notes' },
];

export const DOC_TYPE_SCOPE_PERMISSIONS = [
  { value: 'sales_invoice', permission: READ_SALE_INVOICE, labelKey: 'doc_type_sales_invoice' },
  { value: 'sales_credit_note', permission: READ_SALE_CREDIT_NOTE, labelKey: 'doc_type_sales_credit_note' },
  { value: 'purchase_invoice', permission: READ_PURCHASE_INVOICE, labelKey: 'doc_type_purchase_invoice' },
  {
    value: 'purchase_credit_note',
    permission: READ_PURCHASE_CREDIT_NOTE,
    labelKey: 'doc_type_purchase_credit_note',
  },
];

export const getAllowedDocTypeValues = (hasEInvoicePermission) =>
  DOC_TYPE_SCOPE_PERMISSIONS.filter(({ permission }) => hasEInvoicePermission(permission)).map(({ value }) => value);

export const buildDocTypeOptions = ({ hasEInvoicePermission, filterLabels = {} }) => {
  const allowedValues = new Set(getAllowedDocTypeValues(hasEInvoicePermission));

  const concreteOptions = DOC_TYPE_SCOPE_PERMISSIONS.map(({ value, permission, labelKey }) => ({
    value,
    label: filterLabels[labelKey] ?? value,
    disabled: !hasEInvoicePermission(permission),
  }));

  const hasAnyAllowed = allowedValues.size > 0;

  return [
    {
      value: ALL_FILTER_VALUE,
      label: filterLabels.doc_type_all ?? 'All',
      disabled: !hasAnyAllowed,
    },
    ...concreteOptions,
  ];
};

export const getEnabledOptionValues = (options = []) =>
  options.filter((option) => option.value !== ALL_FILTER_VALUE && !option.disabled).map((option) => option.value);

export const buildTinOptions = (businessProfiles) => {
  const byTin = new Map();

  (Array.isArray(businessProfiles) ? businessProfiles : []).forEach((profile) => {
    const tin = profile?.tin?.trim() || '';
    const name = profile?.party_name || profile?.name || '';

    if (!tin || byTin.has(tin)) {
      return;
    }

    byTin.set(tin, {
      value: tin,
      label: name ? `${tin} — ${truncateLabel(name)}` : tin,
    });
  });

  return Array.from(byTin.values());
};

const truncateLabel = (text, maxLength = 24) => {
  if (!text || text.length <= maxLength) {
    return text;
  }
  return `${text.slice(0, maxLength)}…`;
};

export const buildDefaultDocTypesSelection = (docTypeOptions = []) => {
  const enabledValues = getEnabledOptionValues(docTypeOptions);

  if (enabledValues.length === 0) {
    return [];
  }

  const allEnabled =
    enabledValues.length === DOC_TYPE_SCOPE_PERMISSIONS.length &&
    DOC_TYPE_SCOPE_PERMISSIONS.every(({ value }) => enabledValues.includes(value));

  if (allEnabled) {
    return [ALL_FILTER_VALUE, ...enabledValues];
  }

  return enabledValues;
};

export const pruneDisabledSelections = (selectedValues = [], options = []) => {
  if (!Array.isArray(selectedValues) || selectedValues.length === 0) {
    return selectedValues;
  }

  const disabledValues = new Set(options.filter((option) => option.disabled).map((option) => option.value));

  if (disabledValues.size === 0) {
    return selectedValues;
  }

  const next = selectedValues.filter((value) => !disabledValues.has(value));
  const enabledConcrete = getEnabledOptionValues(options);

  if (next.includes(ALL_FILTER_VALUE)) {
    if (enabledConcrete.length === 0) {
      return [];
    }

    if (enabledConcrete.length === DOC_TYPE_SCOPE_PERMISSIONS.length) {
      return [ALL_FILTER_VALUE, ...enabledConcrete];
    }

    return enabledConcrete;
  }

  return next;
};
